/** @format */
import { Button } from '@mpb-com/feather/components/atoms/button';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { PillLabel } from '@mpb-com/feather/components/atoms/pill-label';
import { useTranslation } from 'next-i18next';
import React, {
    FunctionComponent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import styled from 'styled-components';

import { SearchFacet } from '@toucan/common/services/search/constants';

import { AsideMenu } from '@toucan/client/shared/components/aside-menu';
import { Link } from '@toucan/client/shared/components/link';

import {
    CollapsibleAside,
    CollapsibleAsideVariant,
} from '@toucan/client/blocks/components/collapsible-aside';

import {
    Filter,
    FilterOption,
} from '@toucan/client/features/search/config/types';

import { AccessoryFilters } from '../accessory-filters';
import { ConditionFilter } from '../condition-filter';
import { PriceFilter } from '../price-filter';
import { TEST_ID as dataTestId } from './constants';
import { useResultsAvailable } from './hooks/use-results-available';
import { styles } from './search-filter-menu.styles';
import { SearchFilterMenuProps } from './types';

const StyledSearchFilterMenu = styled('div')`
    ${styles}
`;

export const SearchFilterMenu: FunctionComponent<SearchFilterMenuProps> = ({
    filters,
    filtersAppliedCount,
    onApply,
    baseQuery,
    totalInitialResults,
}: SearchFilterMenuProps) => {
    const { t } = useTranslation();

    const [selectedFilters, setSelectedFilters] = useState<Filter[]>([]);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

    const { data: resultsAvailable, isFetching: isResultsAvailableFetching } =
        useResultsAvailable({
            baseQuery,
            filters: selectedFilters,
        });

    const handleClearFilters = useCallback(() => {
        const filtersWithNoSelectedOptions = selectedFilters.map((filter) => ({
            ...filter,
            selectedOptions: [],
        }));

        // if the menu is closed then we want to apply the search
        if (!isMenuOpen) {
            setSelectedFilters(filtersWithNoSelectedOptions);
            onApply(filtersWithNoSelectedOptions);
            return;
        }

        setSelectedFilters(filtersWithNoSelectedOptions);
    }, [isMenuOpen, onApply, selectedFilters]);

    const handleFilterChange = useCallback(
        (filter: Filter, value: FilterOption[]) => {
            setSelectedFilters((prev) => {
                return prev.map((prevFilter) => {
                    if (prevFilter.id === filter.id) {
                        return { ...prevFilter, selectedOptions: value };
                    }
                    return prevFilter;
                });
            });
        },
        []
    );

    const handleApplyFilters = useCallback(() => {
        onApply(selectedFilters);
    }, [onApply, selectedFilters]);

    const titleExtraContent = useMemo(() => {
        if (filtersAppliedCount === 0) {
            return null;
        }
        return (
            <div
                className="search-filter-menu__title-extra-content"
                data-testid={`${dataTestId}__title-extra-content`}
            >
                <Link
                    onClick={(e) => {
                        e.stopPropagation(); // stops the menu from closing
                        handleClearFilters();
                    }}
                >
                    {t('Clear filters')}
                </Link>
                <PillLabel>
                    {t('{{count}} applied', {
                        count: filtersAppliedCount,
                    })}
                </PillLabel>
            </div>
        );
    }, [filtersAppliedCount, t, handleClearFilters]);

    const searchButtonText = useMemo(() => {
        if (resultsAvailable === 0) {
            return t('No results');
        }
        return t('Show {{count}} products', { count: resultsAvailable });
    }, [resultsAvailable, t]);

    const renderedFilters = useMemo(() => {
        return selectedFilters
            .map((filter) => {
                if (filter.id === SearchFacet.productConditionStarRating) {
                    return (
                        <ConditionFilter
                            key={filter.id}
                            filter={filter}
                            onConditionSelect={(value) => {
                                handleFilterChange(filter, value);
                            }}
                        />
                    );
                }
                if (filter.id === SearchFacet.productPrice) {
                    return (
                        <PriceFilter
                            key={filter.id}
                            filter={filter}
                            onPriceSelect={(value) => {
                                handleFilterChange(filter, value);
                            }}
                        />
                    );
                }
                return;
            })
            .filter((filter) => Boolean(filter));
    }, [selectedFilters, handleFilterChange]);

    const renderedAdvancedFilters = useMemo(() => {
        return selectedFilters
            .map((filter) => {
                if (filter.id === SearchFacet.productPriceModifier) {
                    return (
                        <AccessoryFilters
                            key={filter.id}
                            filter={filter}
                            onAccessorySelect={(value) => {
                                handleFilterChange(filter, value);
                            }}
                            totalInitialResults={totalInitialResults}
                        />
                    );
                }

                return;
            })
            .filter((filter) => Boolean(filter));
    }, [selectedFilters, handleFilterChange, totalInitialResults]);

    const hasAdvancedFilters = useMemo(
        () => Boolean(renderedAdvancedFilters.length),
        [renderedAdvancedFilters]
    );

    useEffect(() => {
        setSelectedFilters([...filters]);
    }, [filters]);

    return (
        <StyledSearchFilterMenu data-testid={dataTestId}>
            <AsideMenu
                data-testid={`${dataTestId}__aside`}
                className="search-filter-menu__aside"
                title={
                    <Heading
                        size={HeadingSize.H3}
                        as={HeadingSize.H2}
                        className="search-filter-menu__heading"
                    >
                        {t('Filters')}
                    </Heading>
                }
                titleExtraContent={titleExtraContent}
                isOpen={isMenuOpen}
                setIsOpen={setIsMenuOpen}
            >
                {renderedFilters}

                {hasAdvancedFilters && (
                    <CollapsibleAside
                        title={t('Advanced filters')}
                        variant={CollapsibleAsideVariant.INLINE}
                        data-testid={`${dataTestId}__aside__advanced-filters`}
                        className="search-filter-menu__advanced-filters"
                    >
                        {renderedAdvancedFilters}
                    </CollapsibleAside>
                )}

                <div
                    className="search-filter-menu__footer"
                    data-testid={`${dataTestId}__footer`}
                >
                    <Button
                        data-testid={`${dataTestId}__apply-filters-button`}
                        isLoading={isResultsAvailableFetching}
                        disabled={
                            isResultsAvailableFetching || resultsAvailable === 0
                        }
                        onClick={() => {
                            handleApplyFilters();
                            setIsMenuOpen(false);
                        }}
                    >
                        {searchButtonText}
                    </Button>
                    {!isResultsAvailableFetching && resultsAvailable === 0 && (
                        <span className="search-filter-menu__no-results-message">
                            {t(
                                'Try removing a few filters to get more options.'
                            )}
                        </span>
                    )}
                    {!isResultsAvailableFetching && (
                        <Link onClick={handleClearFilters}>
                            {t('Clear filters')}
                        </Link>
                    )}
                </div>
            </AsideMenu>
        </StyledSearchFilterMenu>
    );
};
